import React, { useEffect, useState } from "react"
import { useAuth } from "@dfds-platform/business-components"
import { Modal } from '@dfds-ui/modal'
import { EventEmitter } from '@angular/core'
import { Text } from '@dfds-ui/typography'
import { Button } from '@dfds-ui/react-components'
import theme from '@dfds-ui/theme/theme'
import styled from '@emotion/styled'
import { OktaResources } from './okta.provider'

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
`

const BottomInnerWrapper = styled.div`
    margin-top: ${theme.spacing.s};
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
`

const ButtonStyled = styled(Button)`
    width: 35%;
@media only screen and (max-width: 40.063em) {
    width: 100%;
}
`

interface Props {
    cmsData: OktaResources
    onExpiry: EventEmitter<number>
    onClearSession: EventEmitter<boolean>
    onSessionExtended: EventEmitter<boolean>
}

const OktaPopup = (props: Props) => {
    const [loading, setLoading] = useState<boolean>(false)
    const [count, setCount] = useState<number>(-1)
    const [expired, setExpired] = useState<boolean>(false)
    const { authApi } = useAuth()
    let counter: any

    useEffect(() => {
        props.onExpiry.subscribe((windowToRefresh) => {
            setCount(windowToRefresh)
        })
    }, [])

    useEffect(() => {
        if (count > 0) {
            counter = setTimeout(() => {
                setCount(count - 1)
            }, 1000)
        }

        if (count == 0) {
            setExpired(true)
        }
    }, [count])

    const loginResumed = async () => {
        if (!expired) {
            setLoading(true)
            cleanup()
            const newToken = await authApi.renewToken('accessToken') as Token
            localStorage.setItem('sbw_Token', newToken.accessToken)
            props.onSessionExtended.emit(true)

            setLoading(false)
        }
    }

    const clearSession = () => {
        cleanup()
        authApi.logout().then((res) => {
            props.onClearSession.emit(true)
        })
    }

    const cleanup = () => {
        clearTimeout(counter)
        setExpired(false)
        setCount(-1)
    }

    return (
        <Modal
            sizes={{
                s: "95%",
                m: "500px",
                l: "500px",
                xl: "500px",
                xxl: "500px",
            }}
            data-e2e="okta-popup"
            showClose={false}
            isOpen={count > -1 }
            shouldCloseOnEsc={!expired}
            shouldCloseOnOverlayClick={!expired}
            onRequestClose={() => loginResumed()}
            heading={expired ? props.cmsData.oktaSessionExpiredHeader : props.cmsData.oktaSessionAboutToExpireHeader}
        >
            { expired &&
                <Wrapper>
                    <div>
                        <Text as="span" styledAs={'label'}>
                            {props.cmsData.oktaSessionExpiredDescription}
                        </Text>
                    </div>
                    <BottomInnerWrapper>
                        <ButtonStyled onClick={() => clearSession()} variation="secondary" data-testid="start-over">{props.cmsData.oktaStartOverButton}</ButtonStyled>
                    </BottomInnerWrapper>
                </Wrapper>
            }
            { !expired &&
                <Wrapper>
                    <div>
                        <Text as="span" styledAs={'label'}>
                            {props.cmsData.oktaSessionAboutToExpireDescription.replace('{counter}', count.toString())}
                        </Text>
                    </div>
                    <BottomInnerWrapper>
                        <ButtonStyled onClick={() => loginResumed()} submitting={loading} variation="primary" data-testid="resume">{props.cmsData.oktaResumeSessionButton}</ButtonStyled>
                    </BottomInnerWrapper>
                </Wrapper>
            }
        </Modal>
    )
}

export default OktaPopup

interface Token {
    accessToken: string
}
